import React, { useState, useEffect } from 'react';
import ReportCardTemplateService from '../../services/ReportCardTemplateService.js';
import { reportCardService } from '../../services/index.js';
import MarksCardRenderer from '../MarksCardRenderer.js';
import { DATA_SOURCE_REGISTRY } from '../../services/dataSourceRegistry.js';
import { DEFAULT_PART_B_SUBJECTS } from '../reportcard/CoScholasticSection.jsx';
import { 
  FileText, Copy, Star, Save, Printer, Plus, Trash2, CheckCircle2, 
  Settings, Layout, User, Table, CheckSquare, Award, Palette, Eye, ArrowUp, ArrowDown, LayoutGrid, AlertTriangle, Layers
} from 'lucide-react';

const DEFAULT_SYSTEM_PRESETS = [
  {
    id: 'preset_classic',
    templateKey: 'classic_academic',
    templateName: 'Classic Academic',
    category: 'Standard',
    description: 'Formal traditional certificate design with gold accents, shield crest, and classic subject grid.',
    isSystemPreset: true,
    isDefault: true,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'PROGRESS REPORT', layout: 'centered' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '35%', alignment: 'left' },
        { key: 'maxMarks', label: 'Max Marks', dataSource: 'assessment.maxMarks', visible: true, order: 2, width: '15%', alignment: 'center' },
        { key: 'obtainedMarks', label: 'Obtained', dataSource: 'assessment.obtainedMarks', visible: true, order: 3, width: '15%', alignment: 'center' },
        { key: 'percentage', label: 'Percentage', dataSource: 'calculated.percentage', visible: true, order: 4, width: '15%', alignment: 'center' },
        { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 5, width: '20%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#1e3a8a', accentColor: '#f59e0b', fontFamily: 'Serif', borderStyle: 'gold_crest', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_modern',
    templateKey: 'modern_school',
    templateName: 'Modern School',
    category: 'Standard',
    description: 'Modern neo-corporate layout with gradient ribbon header, score badges, and clean rounded cards.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'ACADEMIC TRANSCRIPT', layout: 'banner' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '30%', alignment: 'left' },
        { key: 'fa1', label: 'FA1', dataSource: 'assessment.fa1', examMapping: 'PT-1', visible: true, order: 2, width: '12%', alignment: 'center' },
        { key: 'fa2', label: 'FA2', dataSource: 'assessment.fa2', examMapping: 'PT-2', visible: true, order: 3, width: '12%', alignment: 'center' },
        { key: 'sa1', label: 'SA1', dataSource: 'assessment.sa1', examMapping: 'Mid Term', visible: true, order: 4, width: '12%', alignment: 'center' },
        { key: 'obtainedMarks', label: 'Total', dataSource: 'result.obtainedMarks', visible: true, order: 5, width: '16%', alignment: 'center' },
        { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 6, width: '18%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#2563eb', accentColor: '#10b981', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_primary',
    templateKey: 'primary_progress',
    templateName: 'Primary Progress Card',
    category: 'Primary',
    description: 'Child-friendly progress report for Classes 1-5 with skill badges, attendance meter, and remarks.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: false, showAddress: true, showContact: true, title: 'PRIMARY PROGRESS CARD', layout: 'logo_left' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectName', label: 'Learning Domain / Subject', dataSource: 'subject.name', visible: true, order: 1, width: '45%', alignment: 'left' },
        { key: 'obtainedMarks', label: 'Marks Obtained', dataSource: 'result.obtainedMarks', visible: true, order: 2, width: '20%', alignment: 'center' },
        { key: 'grade', label: 'Performance Grade', dataSource: 'calculated.grade', visible: true, order: 3, width: '35%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: true, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#0d9488', accentColor: '#f43f5e', fontFamily: 'Outfit', borderStyle: 'rounded_playful', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_kindergarten',
    templateKey: 'kindergarten_progress',
    templateName: 'Kindergarten Progress Card',
    category: 'Kindergarten',
    description: 'Qualitative evaluation card for LKG/UKG with domain skills (Language, Numeracy, Motor, Social, Art) and zero hardcoded marks.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: false, showAddress: true, showContact: true, title: 'KINDERGARTEN DEVELOPMENTAL PROGRESS', layout: 'centered' },
      studentInfo: { showPhoto: true, showStudentId: false, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'two_column' },
      columns: [
        { key: 'subjectName', label: 'Developmental Area', dataSource: 'subject.name', visible: true, order: 1, width: '40%', alignment: 'left' },
        { key: 'term1', label: 'Term 1', dataSource: 'assessment.halfYearly', visible: true, order: 2, width: '20%', alignment: 'center' },
        { key: 'term2', label: 'Term 2', dataSource: 'assessment.yearly', visible: true, order: 3, width: '20%', alignment: 'center' },
        { key: 'finalObservation', label: 'Final Observation', dataSource: 'calculated.grade', visible: true, order: 4, width: '20%', alignment: 'center' }
      ],
      domainSkillsList: [
        { category: 'Language & Communication Skills', rating: 'A+' },
        { category: 'Numeracy & Quantitative Thinking', rating: 'A' },
        { category: 'Gross & Fine Motor Skills', rating: 'A+' },
        { category: 'Social & Emotional Development', rating: 'A' },
        { category: 'Art, Music & Creativity', rating: 'A+' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: true, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: false, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#8b5cf6', accentColor: '#ec4899', fontFamily: 'Outfit', borderStyle: 'pastel_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_term_based',
    templateKey: 'term_based_progress',
    templateName: 'Term-Based Progress Card',
    category: 'Term_Based',
    description: 'Structured Term 1 and Term 2 evaluation card with periodic tests, notebook, SEA, and final grade.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'TERM-BASED PROGRESS REPORT', layout: 'logo_left' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columnGroups: [
        {
          groupName: 'TERM 1',
          columns: [
            { key: 't1_pt', label: 'PT (10)', dataSource: 'assessment.periodicTest', examMapping: 'PT-1', visible: true, width: '10%', alignment: 'center' },
            { key: 't1_nb', label: 'NB (5)', dataSource: 'assessment.notebook', visible: true, width: '8%', alignment: 'center' },
            { key: 't1_sea', label: 'SEA (5)', dataSource: 'assessment.subjectEnrichment', visible: true, width: '8%', alignment: 'center' },
            { key: 't1_hy', label: 'Half Yearly (80)', dataSource: 'assessment.halfYearly', examMapping: 'Mid Term', visible: true, width: '14%', alignment: 'center' },
            { key: 't1_tot', label: 'Total (100)', dataSource: 'calculated.total', visible: true, width: '12%', alignment: 'center' }
          ]
        },
        {
          groupName: 'TERM 2',
          columns: [
            { key: 't2_pt', label: 'PT (10)', dataSource: 'assessment.periodicTest', examMapping: 'PT-2', visible: true, width: '10%', alignment: 'center' },
            { key: 't2_nb', label: 'NB (5)', dataSource: 'assessment.notebook', visible: true, width: '8%', alignment: 'center' },
            { key: 't2_sea', label: 'SEA (5)', dataSource: 'assessment.subjectEnrichment', visible: true, width: '8%', alignment: 'center' },
            { key: 't2_ye', label: 'Yearly (80)', dataSource: 'assessment.yearly', examMapping: 'Final Exam', visible: true, width: '14%', alignment: 'center' },
            { key: 't2_tot', label: 'Total (100)', dataSource: 'calculated.total', visible: true, width: '12%', alignment: 'center' }
          ]
        }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#15803d', accentColor: '#0284c7', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_annual',
    templateKey: 'annual_progress',
    templateName: 'Annual Progress Card',
    category: 'Annual',
    description: 'Comprehensive annual progress card combining Term 1, Term 2, co-scholastic, and annual promotion result.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'ANNUAL CUMULATIVE PROGRESS REPORT', layout: 'centered' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '30%', alignment: 'left' },
        { key: 'term1Total', label: 'Term 1 (100)', dataSource: 'assessment.halfYearly', visible: true, order: 2, width: '16%', alignment: 'center' },
        { key: 'term2Total', label: 'Term 2 (100)', dataSource: 'assessment.yearly', visible: true, order: 3, width: '16%', alignment: 'center' },
        { key: 'grandTotal', label: 'Grand Total (200)', dataSource: 'calculated.total', visible: true, order: 4, width: '18%', alignment: 'center' },
        { key: 'grade', label: 'Final Grade', dataSource: 'calculated.grade', visible: true, order: 5, width: '20%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#1e3a8a', accentColor: '#059669', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_state_board',
    templateKey: 'state_board_record',
    templateName: 'State-Board Style Record Sheet',
    category: 'State_Board',
    description: 'Traditional State Board annual record sheet format with monthly, half-yearly, and yearly evaluation.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'STATE BOARD ANNUAL RECORD SHEET', layout: 'logo_left' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showScholarNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '30%', alignment: 'left' },
        { key: 'monthlyEval', label: 'Monthly Eval (20)', dataSource: 'assessment.periodicTest', visible: true, order: 2, width: '15%', alignment: 'center' },
        { key: 'halfYearly', label: 'Half Yearly (30)', dataSource: 'assessment.halfYearly', visible: true, order: 3, width: '15%', alignment: 'center' },
        { key: 'yearlyEval', label: 'Yearly Eval (50)', dataSource: 'assessment.yearly', visible: true, order: 4, width: '15%', alignment: 'center' },
        { key: 'annualTotal', label: 'Annual Total (100)', dataSource: 'calculated.total', visible: true, order: 5, width: '15%', alignment: 'center' },
        { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 6, width: '10%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
      style: { primaryColor: '#b91c1c', accentColor: '#d97706', fontFamily: 'Serif', borderStyle: 'formal_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_pu_college',
    templateKey: 'pu_college',
    templateName: 'Senior Secondary / PU College Transcript',
    category: 'PU_College',
    description: 'Specialized 2-year Pre-University / Class 11-12 layout with Theory, Practical, Total Marks, and Grade credits.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'STATEMENT OF MARKS & CREDIT GRADES', layout: 'centered' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectCode', label: 'Code', dataSource: 'subject.code', visible: true, order: 1, width: '12%', alignment: 'center' },
        { key: 'subjectName', label: 'Subject Title', dataSource: 'subject.name', visible: true, order: 2, width: '32%', alignment: 'left' },
        { key: 'theoryMarks', label: 'Theory', dataSource: 'result.theoryMarks', visible: true, order: 3, width: '12%', alignment: 'center' },
        { key: 'practicalMarks', label: 'Practical', dataSource: 'result.practicalMarks', visible: true, order: 4, width: '12%', alignment: 'center' },
        { key: 'obtainedMarks', label: 'Total Marks', dataSource: 'result.obtainedMarks', visible: true, order: 5, width: '16%', alignment: 'center' },
        { key: 'credits', label: 'Credits', dataSource: 'subject.credits', visible: true, order: 6, width: '8%', alignment: 'center' },
        { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 7, width: '8%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: false, showCoCurricular: false, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: false },
      style: { primaryColor: '#0f172a', accentColor: '#3b82f6', fontFamily: 'Inter', borderStyle: 'formal_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
    })
  },
  {
    id: 'preset_pu_college',
    templateKey: 'pu_college',
    templateName: 'PU / College Marks Statement',
    category: 'PU_College',
    description: 'Official university transcript format with subject codes, Theory/Practical split, SGPA/CGPA, and credits.',
    isSystemPreset: true,
    isDefault: false,
    configJson: JSON.stringify({
      header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'STATEMENT OF MARKS & CREDIT GRADES', layout: 'centered' },
      studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
      columns: [
        { key: 'subjectCode', label: 'Code', dataSource: 'subject.code', visible: true, order: 1, width: '12%', alignment: 'center' },
        { key: 'subjectName', label: 'Subject Title', dataSource: 'subject.name', visible: true, order: 2, width: '32%', alignment: 'left' },
        { key: 'theoryMarks', label: 'Theory', dataSource: 'result.theoryMarks', visible: true, order: 3, width: '12%', alignment: 'center' },
        { key: 'practicalMarks', label: 'Practical', dataSource: 'result.practicalMarks', visible: true, order: 4, width: '12%', alignment: 'center' },
        { key: 'obtainedMarks', label: 'Total Marks', dataSource: 'result.obtainedMarks', visible: true, order: 5, width: '16%', alignment: 'center' },
        { key: 'credits', label: 'Credits', dataSource: 'subject.credits', visible: true, order: 6, width: '8%', alignment: 'center' },
        { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 7, width: '8%', alignment: 'center' }
      ],
      additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: false, showCoCurricular: false, showPromotion: true, showGradingScale: true, showQR: true },
      customSections: [],
      footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: false },
      style: { primaryColor: '#0f172a', accentColor: '#0284c7', fontFamily: 'Roboto', borderStyle: 'university_grid', photoPosition: 'top-right' }
    })
  }
];

const ReportCardTemplateDesigner = ({
  orgId = 'SCH-02',
  academicYear = '2026-27',
  examTypeConfigs = [],
  branding = {},
  orgName = '',
  orgAddress = ''
}) => {
  const [templates, setTemplates] = useState(DEFAULT_SYSTEM_PRESETS);
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_SYSTEM_PRESETS[0]);
  const [activeTab, setActiveTab] = useState('columns');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [useRealStudent, setUseRealStudent] = useState(false);
  const [examTypesList, setExamTypesList] = useState(examTypeConfigs || []);

  useEffect(() => {
    if (examTypeConfigs && examTypeConfigs.length > 0) {
      setExamTypesList(examTypeConfigs);
    } else {
      reportCardService.getExamTypeConfig(academicYear)
        .then(res => {
          if (res && res.success && Array.isArray(res.examTypes)) {
            setExamTypesList(res.examTypes);
          }
        })
        .catch(() => {});
    }
  }, [examTypeConfigs, academicYear]);

  // Active configuration state being edited
  const [config, setConfig] = useState(JSON.parse(DEFAULT_SYSTEM_PRESETS[0].configJson));

  const classList = ['LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'PUC-1', 'PUC-2'];

  useEffect(() => {
    loadTemplatesAndAssignments();
  }, [orgId]);

  const loadTemplatesAndAssignments = async (preserveKey = null) => {
    setLoading(true);
    try {
      const targetKey = preserveKey || selectedTemplate?.templateKey;
      const tList = await ReportCardTemplateService.getTemplatesByOrg(orgId);
      if (Array.isArray(tList) && tList.length > 0) {
        const merged = [...DEFAULT_SYSTEM_PRESETS];
        tList.forEach(fetched => {
          const existsIdx = merged.findIndex(m => m.templateKey === fetched.templateKey || (m.id && fetched.id && m.id === fetched.id));
          if (existsIdx >= 0) {
            merged[existsIdx] = { ...merged[existsIdx], ...fetched };
          } else {
            merged.push(fetched);
          }
        });
        setTemplates(merged);
        const match = targetKey ? merged.find(t => t.templateKey === targetKey) : null;
        const def = match || merged.find(t => t.isDefault && !t.isSystemPreset) || merged.find(t => t.isDefault) || merged[0];
        selectTemplate(def);
      } else {
        setTemplates(DEFAULT_SYSTEM_PRESETS);
        selectTemplate(DEFAULT_SYSTEM_PRESETS[0]);
      }

      const aList = await ReportCardTemplateService.getAssignments(orgId);
      setAssignments(Array.isArray(aList) ? aList : []);
    } catch (err) {
      setTemplates(DEFAULT_SYSTEM_PRESETS);
      selectTemplate(DEFAULT_SYSTEM_PRESETS[0]);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (tmpl) => {
    if (!tmpl) return;
    setSelectedTemplate(tmpl);
    if (tmpl.configJson) {
      try {
        const parsed = typeof tmpl.configJson === 'string' ? JSON.parse(tmpl.configJson) : tmpl.configJson;
        setConfig(parsed);
      } catch (e) {
        console.error('Error parsing configJson:', e);
      }
    }
  };

  const showNotify = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleDuplicate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const copyPayload = {
        orgId,
        templateKey: `${selectedTemplate.templateKey}_custom_${Date.now()}`,
        templateName: `${selectedTemplate.templateName} (Custom Copy)`,
        category: selectedTemplate.category || 'Standard',
        description: `Customized copy of ${selectedTemplate.templateName}`,
        isDefault: false,
        configJson: JSON.stringify(config)
      };
      const copy = await ReportCardTemplateService.saveTemplate(copyPayload);
      showNotify('success', 'Custom editable template copy created and saved!');
      await loadTemplatesAndAssignments(copy?.templateKey);
    } catch (err) {
      showNotify('error', 'Failed to duplicate template: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      // 1. Save template with current modified config and isDefault: true
      const savePayload = {
        id: typeof selectedTemplate.id === 'number' ? selectedTemplate.id : null,
        orgId,
        templateKey: selectedTemplate.templateKey,
        templateName: selectedTemplate.templateName,
        category: selectedTemplate.category || 'Standard',
        description: selectedTemplate.description || 'School default template',
        isDefault: true,
        configJson: JSON.stringify(config)
      };
      const saved = await ReportCardTemplateService.saveTemplate(savePayload);

      // 2. Also ensure setDefaultTemplate activates it
      const targetId = saved?.id || (typeof selectedTemplate.id === 'number' ? selectedTemplate.id : selectedTemplate.templateKey);
      await ReportCardTemplateService.setDefaultTemplate(targetId, orgId);

      showNotify('success', `"${selectedTemplate.templateName}" successfully saved and set as your school's default report card format!`);
      await loadTemplatesAndAssignments(selectedTemplate.templateKey);
    } catch (err) {
      showNotify('error', 'Failed to set default template: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const payload = {
        id: typeof selectedTemplate.id === 'number' ? selectedTemplate.id : null,
        orgId,
        templateKey: selectedTemplate.templateKey,
        templateName: selectedTemplate.templateName,
        category: selectedTemplate.category || 'Standard',
        description: selectedTemplate.description || 'Customized template',
        isDefault: Boolean(selectedTemplate.isDefault),
        configJson: JSON.stringify(config)
      };
      await ReportCardTemplateService.saveTemplate(payload);
      showNotify('success', `"${selectedTemplate.templateName}" design configuration saved successfully!`);
      await loadTemplatesAndAssignments(selectedTemplate.templateKey);
    } catch (err) {
      showNotify('error', 'Failed to save template configuration: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  // Add Custom Section Handler
  const handleAddCustomSection = (type) => {
    const newSection = {
      id: `csec_${Date.now()}`,
      title: type === 'text' ? 'Institutional Notice' : type === 'key_value' ? 'Additional Metadata' : type === 'assessment_table' ? 'Co-Scholastic Assessment' : 'Custom Remarks',
      type: type, // 'text', 'key_value', 'remarks', 'assessment_table'
      content: 'Enter custom section content or policy details here.',
      items: type === 'key_value' ? [{ key: 'House', value: 'Tagore House' }, { key: 'Blood Group', value: 'O+' }] : type === 'assessment_table' ? [{ activity: 'Work Education', grade: 'A+' }, { activity: 'Art Education', grade: 'A' }, { activity: 'Health & Physical Education', grade: 'A+' }] : []
    };
    const updated = [...(config.customSections || []), newSection];
    setConfig({ ...config, customSections: updated });
  };

  const handleRemoveCustomSection = (idx) => {
    const updated = (config.customSections || []).filter((_, i) => i !== idx);
    setConfig({ ...config, customSections: updated });
  };

  // Dynamic Column Handlers
  const handleAddColumn = () => {
    const newCol = {
      key: `custom_${Date.now()}`,
      label: 'New Column',
      dataSource: 'result.obtainedMarks',
      examMapping: 'PT-1',
      visible: true,
      order: (config.columns || []).length + 1,
      width: '15%',
      alignment: 'center'
    };
    setConfig({ ...config, columns: [...(config.columns || []), newCol] });
  };

  const handleRemoveColumn = (index) => {
    const updated = config.columns.filter((_, i) => i !== index);
    setConfig({ ...config, columns: updated });
  };

  const handleColumnChange = (index, field, value) => {
    const updated = [...config.columns];
    updated[index] = { ...updated[index], [field]: value };
    setConfig({ ...config, columns: updated });
  };

  const handleMoveColumn = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === config.columns.length - 1)) return;
    const updated = [...config.columns];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    updated.forEach((c, idx) => c.order = idx + 1);
    setConfig({ ...config, columns: updated });
  };

  // Class Mapping Toggle
  const isClassAssigned = (className) => {
    return assignments.some(a => (a.template?.id === selectedTemplate?.id || a.templateId === selectedTemplate?.id) && a.className === className);
  };

  const toggleClassAssignment = (className) => {
    if (!selectedTemplate) return;
    let updated;
    if (isClassAssigned(className)) {
      updated = assignments.filter(a => !( (a.template?.id === selectedTemplate.id || a.templateId === selectedTemplate.id) && a.className === className ));
    } else {
      updated = [...assignments, { templateId: selectedTemplate.id, className, academicYear: '2026-27', priority: 1 }];
    }
    setAssignments(updated);
  };

  const handleSaveAssignments = async () => {
    setSaving(true);
    try {
      const payload = assignments.map(a => ({
        templateId: a.templateId || a.template?.id,
        className: a.className,
        academicYear: a.academicYear || '2026-27',
        priority: a.priority || 1
      }));
      await ReportCardTemplateService.saveAssignments(orgId, payload);
      showNotify('success', 'Class-level template mappings saved successfully!');
    } catch (err) {
      showNotify('error', 'Failed to save class mappings');
    } finally {
      setSaving(false);
    }
  };

  const handlePrintPreview = () => {
    const element = document.getElementById('marks-card-designer-preview');
    if (!element) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Report Card Print Test</title>
          <style>
            @page { size: A4 portrait; margin: 0; }
            body { margin: 0; padding: 0; background: #fff; }
          </style>
        </head>
        <body>
          ${element.outerHTML}
          <script>
            window.onload = function() { window.print(); window.close(); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div style={{ background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      
      {/* ── TOP CONTROL TOOLBAR ── */}
      <div style={{ background: '#0f172a', padding: '16px 20px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FileText size={24} color="#38bdf8" />
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Indian School Progress Card Designer</h3>
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>Controlled Data-Source Registry & Exam-to-Column Mapping Engine</span>
          </div>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedTemplate?.isDefault ? (
            <span style={{ fontSize: '11px', padding: '5px 12px', background: '#eab308', color: '#0f172a', borderRadius: '4px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Star size={13} fill="#0f172a" /> Active School Default
            </span>
          ) : selectedTemplate?.isSystemPreset ? (
            <span style={{ fontSize: '11px', padding: '4px 10px', background: '#334155', color: '#cbd5e1', borderRadius: '4px', fontWeight: '700' }}>
              Starter Preset
            </span>
          ) : (
            <span style={{ fontSize: '11px', padding: '4px 10px', background: '#15803d', color: '#dcfce7', borderRadius: '4px', fontWeight: '700' }}>
              ✏️ Custom Editable
            </span>
          )}

          <button
            onClick={() => setUseRealStudent(!useRealStudent)}
            style={{ padding: '6px 12px', background: useRealStudent ? '#7e22ce' : '#334155', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
          >
            {useRealStudent ? '👤 Real Student (Read-Only)' : '🧪 Sample Mock Student'}
          </button>

          <button
            onClick={handleDuplicate}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
          >
            <Copy size={14} /> Duplicate / Copy
          </button>

          <button
            onClick={handleSaveConfig}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}
          >
            <Save size={14} /> {saving ? 'Saving...' : 'Save Design'}
          </button>

          <button
            onClick={handleSetDefault}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#eab308', color: '#0f172a', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 2px 6px rgba(234,179,8,0.3)' }}
          >
            <Star size={14} fill={selectedTemplate?.isDefault ? '#0f172a' : 'none'} /> {selectedTemplate?.isDefault ? 'Save & Update Default' : 'Save & Set as Default'}
          </button>

          <button
            onClick={handlePrintPreview}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#64748b', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
          >
            <Printer size={14} /> Print Test
          </button>
        </div>
      </div>

      {/* ── BUILT-IN TEMPLATE FORMAT PICKER BAR ── */}
      <div style={{ background: '#1e293b', padding: '12px 20px', borderTop: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '6px' }}>
          <LayoutGrid size={15} color="#38bdf8" /> Starter Presets:
        </span>
        
        {templates.map(t => {
          const isSelected = selectedTemplate?.id === t.id || selectedTemplate?.templateKey === t.templateKey;
          return (
            <button
              key={t.id || t.templateKey}
              onClick={() => selectTemplate(t)}
              style={{
                padding: '7px 14px',
                borderRadius: '6px',
                border: isSelected ? '2px solid #38bdf8' : '1px solid #475569',
                background: isSelected ? '#0284c7' : '#0f172a',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: isSelected ? '0 0 10px rgba(56, 189, 248, 0.4)' : 'none'
              }}
            >
              {t.templateName}
              {t.isDefault && <span style={{ fontSize: '10px', color: '#fef08a' }}>⭐</span>}
              {t.isSystemPreset && <span style={{ fontSize: '9px', opacity: 0.7 }}>🔒</span>}
            </button>
          );
        })}
      </div>

      {/* Notification Bar */}
      {notification && (
        <div style={{ padding: '10px 20px', background: notification.type === 'success' ? '#dcfce7' : '#fee2e2', color: notification.type === 'success' ? '#166534' : '#991b1b', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #cbd5e1' }}>
          {notification.text}
        </div>
      )}

      {/* ── MAIN WORKSPACE: SPLIT CONFIGURATOR (LEFT) & LIVE PREVIEW (RIGHT) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '480px 1fr', gap: '20px', padding: '20px' }}>
        
        {/* LEFT PANEL: TABBED CONFIGURATOR */}
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '16px', display: 'flex', flexDirection: 'column' }}>
          
          {/* Sub-tab Navigation */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
            <button onClick={() => setActiveTab('columns')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'columns' ? '#2563eb' : '#f1f5f9', color: activeTab === 'columns' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Table size={12} /> Data Columns
            </button>
            <button onClick={() => setActiveTab('custom_sections')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'custom_sections' ? '#2563eb' : '#f1f5f9', color: activeTab === 'custom_sections' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Layers size={12} /> Custom Sections
            </button>
            <button onClick={() => setActiveTab('header')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'header' ? '#2563eb' : '#f1f5f9', color: activeTab === 'header' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Layout size={12} /> Header
            </button>
            <button onClick={() => setActiveTab('student')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'student' ? '#2563eb' : '#f1f5f9', color: activeTab === 'student' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <User size={12} /> Student Info
            </button>
            <button onClick={() => setActiveTab('sections')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'sections' ? '#2563eb' : '#f1f5f9', color: activeTab === 'sections' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CheckSquare size={12} /> Sections
            </button>
            <button onClick={() => setActiveTab('footer')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'footer' ? '#2563eb' : '#f1f5f9', color: activeTab === 'footer' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Award size={12} /> Footer
            </button>
            <button onClick={() => setActiveTab('style')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'style' ? '#2563eb' : '#f1f5f9', color: activeTab === 'style' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Palette size={12} /> Style
            </button>
            <button onClick={() => setActiveTab('mapping')} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: activeTab === 'mapping' ? '#2563eb' : '#f1f5f9', color: activeTab === 'mapping' ? '#fff' : '#475569', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Settings size={12} /> Class Mapping
            </button>
          </div>

          {/* ── TAB 1: DATA COLUMNS BUILDER WITH CONTROLLED REGISTRY & EXAM MAPPING ── */}
          {activeTab === 'columns' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Marks Table Columns (Data-Bound Registry)</h4>
                <button onClick={handleAddColumn} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                  <Plus size={12} /> Add Column
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                {(config.columns || []).map((col, idx) => (
                  <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <input
                        type="text"
                        value={col.label}
                        onChange={(e) => handleColumnChange(idx, 'label', e.target.value)}
                        placeholder="Column Header Label"
                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: '700', flex: 1, marginRight: '8px' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button onClick={() => handleMoveColumn(idx, -1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><ArrowUp size={14} /></button>
                        <button onClick={() => handleMoveColumn(idx, 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><ArrowDown size={14} /></button>
                        <button onClick={() => handleRemoveColumn(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                      <div>
                        <label style={{ fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Controlled Data Source:</label>
                        <select
                          value={col.dataSource}
                          onChange={(e) => handleColumnChange(idx, 'dataSource', e.target.value)}
                          style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                        >
                          {DATA_SOURCE_REGISTRY.map(ds => (
                            <option key={ds.key} value={ds.key}>[{ds.category}] {ds.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{ fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Mapped Examination:</label>
                        <input
                          type="text"
                          list="examTypeCatalogOptions"
                          value={col.examMapping || ''}
                          onChange={(e) => handleColumnChange(idx, 'examMapping', e.target.value)}
                          placeholder="e.g. FA-1 / FA-2 / SA-1 / FA-3 / FA-4 / SA-2"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1.5px solid #cbd5e1', fontSize: '11px', background: '#ffffff' }}
                        />
                        <datalist id="examTypeCatalogOptions">
                          {examTypesList.map(et => (
                            <option key={et.examCode} value={et.examCode}>
                              {et.examCode} — {et.examName || et.examCode} ({et.termLabel || 'Assessment'})
                            </option>
                          ))}
                        </datalist>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB 2: CUSTOM SECTIONS BUILDER ── */}
          {activeTab === 'custom_sections' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Custom Report Card Sections</h4>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => handleAddCustomSection('text')} style={{ padding: '3px 6px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>+ Text</button>
                  <button onClick={() => handleAddCustomSection('key_value')} style={{ padding: '3px 6px', background: '#059669', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>+ Grid</button>
                  <button onClick={() => handleAddCustomSection('assessment_table')} style={{ padding: '3px 6px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>+ Table</button>
                  <button onClick={() => handleAddCustomSection('remarks')} style={{ padding: '3px 6px', background: '#7e22ce', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' }}>+ Remarks</button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
                {(config.customSections || []).map((sec, idx) => (
                  <div key={idx} style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <input
                        type="text"
                        value={sec.title}
                        onChange={(e) => {
                          const updated = [...config.customSections];
                          updated[idx].title = e.target.value;
                          setConfig({ ...config, customSections: updated });
                        }}
                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', fontWeight: '700' }}
                      />
                      <button onClick={() => handleRemoveCustomSection(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>
                    </div>

                    {sec.type === 'text' && (
                      <textarea
                        value={sec.content}
                        onChange={(e) => {
                          const updated = [...config.customSections];
                          updated[idx].content = e.target.value;
                          setConfig({ ...config, customSections: updated });
                        }}
                        rows={2}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                      />
                    )}

                    {sec.type === 'remarks' && (
                      <textarea
                        value={sec.content}
                        onChange={(e) => {
                          const updated = [...config.customSections];
                          updated[idx].content = e.target.value;
                          setConfig({ ...config, customSections: updated });
                        }}
                        rows={2}
                        placeholder="Remarks content..."
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                      />
                    )}

                    {sec.type === 'key_value' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                        {(sec.items || []).map((item, itemIdx) => (
                          <div key={itemIdx} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={item.key}
                              placeholder="Key"
                              onChange={(e) => {
                                const updated = [...config.customSections];
                                updated[idx].items[itemIdx].key = e.target.value;
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ flex: 1, padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            />
                            <input
                              type="text"
                              value={item.value}
                              placeholder="Value"
                              onChange={(e) => {
                                const updated = [...config.customSections];
                                updated[idx].items[itemIdx].value = e.target.value;
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ flex: 1, padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            />
                            <button
                              onClick={() => {
                                const updated = [...config.customSections];
                                updated[idx].items = updated[idx].items.filter((_, i) => i !== itemIdx);
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const updated = [...config.customSections];
                            updated[idx].items = [...(updated[idx].items || []), { key: 'Key', value: 'Value' }];
                            setConfig({ ...config, customSections: updated });
                          }}
                          style={{ alignSelf: 'flex-start', padding: '2px 6px', background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: '700', cursor: 'pointer' }}
                        >
                          + Add Item
                        </button>
                      </div>
                    )}

                    {sec.type === 'assessment_table' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                        {(sec.items || []).map((item, itemIdx) => (
                          <div key={itemIdx} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={item.activity}
                              placeholder="Activity / Area"
                              onChange={(e) => {
                                const updated = [...config.customSections];
                                updated[idx].items[itemIdx].activity = e.target.value;
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ flex: 1, padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            />
                            <input
                              type="text"
                              value={item.grade}
                              placeholder="Grade / Level"
                              onChange={(e) => {
                                const updated = [...config.customSections];
                                updated[idx].items[itemIdx].grade = e.target.value;
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ flex: 1, padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            />
                            <button
                              onClick={() => {
                                const updated = [...config.customSections];
                                updated[idx].items = updated[idx].items.filter((_, i) => i !== itemIdx);
                                setConfig({ ...config, customSections: updated });
                              }}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            const updated = [...config.customSections];
                            updated[idx].items = [...(updated[idx].items || []), { activity: 'Activity Name', grade: 'A' }];
                            setConfig({ ...config, customSections: updated });
                          }}
                          style={{ alignSelf: 'flex-start', padding: '2px 6px', background: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: '700', cursor: 'pointer' }}
                        >
                          + Add Row
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB 3: HEADER CONFIG ── */}
          {activeTab === 'header' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12px' }}>
              <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Header Layout & Visibility</h4>
              
              <div>
                <label style={{ fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Header Layout Preset:</label>
                <select
                  value={config.header?.layout || 'centered'}
                  onChange={(e) => setConfig({ ...config, header: { ...config.header, layout: e.target.value } })}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                >
                  <option value="centered">Centered Institutional Header</option>
                  <option value="logo_left">Logo Left + Title Right</option>
                  <option value="banner">Vibrant Corporate Gradient Banner</option>
                  <option value="compact">Compact Minimal Certificate</option>
                </select>
              </div>

              <div>
                <label style={{ fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Report Card Title Override:</label>
                <input
                  type="text"
                  value={config.header?.title || ''}
                  onChange={(e) => setConfig({ ...config, header: { ...config.header, title: e.target.value } })}
                  placeholder="e.g. PROGRESS REPORT / STATEMENT OF MARKS"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                />
              </div>

              {['showLogo', 'showSchoolName', 'showAffiliationNo', 'showAddress', 'showContact'].map((key) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!config.header?.[key]}
                    onChange={(e) => setConfig({ ...config, header: { ...config.header, [key]: e.target.checked } })}
                  />
                  <span style={{ fontWeight: '600' }}>Show {key.replace('show', '')}</span>
                </label>
              ))}
            </div>
          )}

          {/* ── TAB 4: STUDENT INFO CONFIG ── */}
          {activeTab === 'student' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
              <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Student Information Fields</h4>
              {Object.keys(config.studentInfo || {}).map((key) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!config.studentInfo?.[key]}
                    onChange={(e) => setConfig({ ...config, studentInfo: { ...config.studentInfo, [key]: e.target.checked } })}
                  />
                  <span style={{ fontWeight: '600' }}>Show {key.replace('show', '')}</span>
                </label>
              ))}
            </div>
          )}

          {/* ── TAB 5: ADDITIONAL SECTIONS ── */}
          {activeTab === 'sections' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Additional Sections & Evaluation Cards</h4>
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: '#64748b' }}>
                Toggle which evaluation sections and institutional cards appear on the printed marks card:
              </p>

              {[
                { key: 'showPartB', label: 'Part B: Co-Scholastic Area (B. Co-Scholastic Area)', icon: '🎨', defaultOff: true },
                { key: 'showAttendance', label: 'Attendance Record (Working Days, Present, %)', icon: '📅' },
                { key: 'showTeacherRemarks', label: 'Class Teacher Remarks Box', icon: '✍️' },
                { key: 'showPrincipalRemarks', label: 'Principal Remarks & Observation Box', icon: '🎓' },
                { key: 'showDomainSkills', label: 'Developmental Skills / Ability Rubric', icon: '🌟' },
                { key: 'showBehaviour', label: 'Student Discipline & Values Rating', icon: '🛡️' },
                { key: 'showPromotion', label: 'Promotion Decision Card', icon: '🏆' },
                { key: 'showGradingScale', label: 'Institution Grading Scale Reference', icon: '📐' },
                { key: 'showQR', label: 'Digital Verification QR Code', icon: '📱' },
                { key: 'showResultSummary', label: 'Overall Result Summary Box (Total, %, Grade)', icon: '📊' }
              ].map(item => {
                const isChecked = item.defaultOff 
                  ? Boolean(config.additionalSections?.[item.key])
                  : config.additionalSections?.[item.key] !== false;
                return (
                  <label
                    key={item.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      background: isChecked ? '#eff6ff' : '#f8fafc',
                      border: isChecked ? '1.5px solid #93c5fd' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const updatedSections = {
                          ...(config.additionalSections || {}),
                          [item.key]: e.target.checked
                        };
                        setConfig({
                          ...config,
                          additionalSections: updatedSections
                        });
                      }}
                      style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '15px' }}>{item.icon}</span>
                    <span style={{ fontWeight: '700', color: isChecked ? '#1e40af' : '#475569', fontSize: '12.5px' }}>
                      {item.label}
                    </span>
                  </label>
                );
              })}

              {/* ── PART B (CO-SCHOLASTIC) SUBJECTS MANAGER ── */}
              {Boolean(config.additionalSections?.showPartB) && (
                <div style={{ marginTop: '10px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong style={{ fontSize: '12px', color: '#166534' }}>Part B Subjects Configuration</strong>
                      <div style={{ fontSize: '11px', color: '#15803d' }}>Subjects included under B. Co-Scholastic Area</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const currentList = (config.coScholasticList && config.coScholasticList.length > 0)
                          ? config.coScholasticList
                          : DEFAULT_PART_B_SUBJECTS;
                        setConfig({
                          ...config,
                          coScholasticList: [...currentList, { activity: 'New Activity', term1: 'A', term2: 'A' }]
                        });
                      }}
                      style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '700', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      + Add Subject
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {((config.coScholasticList && config.coScholasticList.length > 0) ? config.coScholasticList : DEFAULT_PART_B_SUBJECTS).map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', padding: '6px 10px', borderRadius: '6px', border: '1px solid #dcfce7' }}>
                        <input
                          type="text"
                          value={item.activity || ''}
                          onChange={(e) => {
                            const currentList = [...((config.coScholasticList && config.coScholasticList.length > 0) ? config.coScholasticList : DEFAULT_PART_B_SUBJECTS)];
                            currentList[idx] = { ...currentList[idx], activity: e.target.value };
                            setConfig({ ...config, coScholasticList: currentList });
                          }}
                          placeholder="Subject Name"
                          style={{ flex: 1, padding: '4px 8px', fontSize: '11.5px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const currentList = [...((config.coScholasticList && config.coScholasticList.length > 0) ? config.coScholasticList : DEFAULT_PART_B_SUBJECTS)];
                            currentList.splice(idx, 1);
                            setConfig({ ...config, coScholasticList: currentList });
                          }}
                          style={{ padding: '3px 6px', background: '#fee2e2', border: 'none', borderRadius: '4px', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}
                          title="Remove Subject"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB 6: FOOTER CONFIG ── */}
          {activeTab === 'footer' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
              <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Footer Signatures & Seals</h4>
              {Object.keys(config.footer || {}).map((key) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!config.footer?.[key]}
                    onChange={(e) => setConfig({ ...config, footer: { ...config.footer, [key]: e.target.checked } })}
                  />
                  <span style={{ fontWeight: '600' }}>Show {key.replace('show', '')}</span>
                </label>
              ))}
            </div>
          )}

          {/* ── TAB 7: CONTROLLED STYLE CONFIG ── */}
          {activeTab === 'style' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12px' }}>
              <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Controlled Style Tokens</h4>
              
              <div>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Primary Theme Color:</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="color" value={config.style?.primaryColor || '#1e3a8a'} onChange={(e) => setConfig({ ...config, style: { ...config.style, primaryColor: e.target.value } })} style={{ width: '40px', height: '32px', padding: 0, border: 'none', cursor: 'pointer' }} />
                  <input type="text" value={config.style?.primaryColor || '#1e3a8a'} onChange={(e) => setConfig({ ...config, style: { ...config.style, primaryColor: e.target.value } })} style={{ flex: 1, padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
              </div>

              <div>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Typography Font Family:</label>
                <select value={config.style?.fontFamily || 'Inter'} onChange={(e) => setConfig({ ...config, style: { ...config.style, fontFamily: e.target.value } })} style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  <option value="Inter">Inter (Clean Modern)</option>
                  <option value="Roboto">Roboto (Corporate)</option>
                  <option value="Serif">Georgia (Classic Certificate)</option>
                  <option value="Outfit">Outfit (Playful School)</option>
                </select>
              </div>

              <div>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Student Photo Position:</label>
                <select value={config.style?.photoPosition || 'top-right'} onChange={(e) => setConfig({ ...config, style: { ...config.style, photoPosition: e.target.value } })} style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  <option value="top-right">Top Right Header</option>
                  <option value="top-left">Top Left Header</option>
                  <option value="none">Hidden</option>
                </select>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '700', color: '#1e293b' }}>
                  <input
                    type="checkbox"
                    checked={config.style?.showWatermark !== false}
                    onChange={(e) => setConfig({ ...config, style: { ...config.style, showWatermark: e.target.checked } })}
                    style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                  />
                  <span>🛡️ Show Background School Watermark (Light Color)</span>
                </label>

                {config.style?.showWatermark !== false && (
                  <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#f8fafc', padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    <div>
                      <label style={{ fontWeight: '600', color: '#475569', display: 'block', marginBottom: '2px', fontSize: '11px' }}>Watermark Style / Type:</label>
                      <select
                        value={config.style?.watermarkType || 'crest'}
                        onChange={(e) => setConfig({ ...config, style: { ...config.style, watermarkType: e.target.value } })}
                        style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                      >
                        <option value="crest">👑 Classic Academic Shield Crest (Ornate)</option>
                        <option value="logo">🏫 Official School Logo (Grayscale)</option>
                        <option value="monogram">⭕ Circular Institutional Monogram</option>
                        <option value="diagonal_text">📐 Diagonal Official Banner</option>
                      </select>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                        <span>Watermark Opacity (Lightness):</span>
                        <strong style={{ color: '#2563eb' }}>{Math.round((config.style?.watermarkOpacity !== undefined ? config.style.watermarkOpacity : 0.035) * 100)}%</strong>
                      </div>
                      <input
                        type="range"
                        min="0.01"
                        max="0.15"
                        step="0.005"
                        value={config.style?.watermarkOpacity !== undefined ? config.style.watermarkOpacity : 0.035}
                        onChange={(e) => setConfig({ ...config, style: { ...config.style, watermarkOpacity: parseFloat(e.target.value) } })}
                        style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }}
                      />
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        {[{ label: 'Ultra Faint (2%)', val: 0.02 }, { label: 'Subtle (3.5%)', val: 0.035 }, { label: 'Light (5%)', val: 0.05 }, { label: 'Medium (8%)', val: 0.08 }].map(chip => (
                          <button
                            key={chip.val}
                            type="button"
                            onClick={() => setConfig({ ...config, style: { ...config.style, watermarkOpacity: chip.val } })}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: '1px solid #cbd5e1',
                              background: (config.style?.watermarkOpacity === chip.val || (config.style?.watermarkOpacity === undefined && chip.val === 0.035)) ? '#2563eb' : '#fff',
                              color: (config.style?.watermarkOpacity === chip.val || (config.style?.watermarkOpacity === undefined && chip.val === 0.035)) ? '#fff' : '#475569',
                              fontSize: '9.5px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontWeight: '600', color: '#475569', display: 'block', marginBottom: '2px', fontSize: '11px' }}>Watermark Dimensions:</label>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {[{ label: 'Medium (320px)', val: 320 }, { label: 'Standard (380px)', val: 380 }, { label: 'Large (450px)', val: 450 }].map(size => (
                          <button
                            key={size.val}
                            type="button"
                            onClick={() => setConfig({ ...config, style: { ...config.style, watermarkSize: size.val } })}
                            style={{
                              flex: 1,
                              padding: '3px 6px',
                              borderRadius: '4px',
                              border: '1px solid #cbd5e1',
                              background: (config.style?.watermarkSize === size.val || (config.style?.watermarkSize === undefined && size.val === 380)) ? '#2563eb' : '#fff',
                              color: (config.style?.watermarkSize === size.val || (config.style?.watermarkSize === undefined && size.val === 380)) ? '#fff' : '#475569',
                              fontSize: '10px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            {size.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 8: CLASS-LEVEL ASSIGNMENT MAPPING ── */}
          {activeTab === 'mapping' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>Class Level Mapping</h4>
                <button onClick={handleSaveAssignments} disabled={saving} style={{ padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                  Save Mapping
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                Check which class levels should automatically resolve to <strong>"{selectedTemplate?.templateName}"</strong>:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', maxHeight: '350px', overflowY: 'auto' }}>
                {classList.map(cls => {
                  const checked = isClassAssigned(cls);
                  return (
                    <label key={cls} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: checked ? '#dbeafe' : '#f8fafc', border: `1px solid ${checked ? '#93c5fd' : '#e2e8f0'}`, borderRadius: '6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleClassAssignment(cls)} />
                      <span style={{ fontWeight: '700', color: checked ? '#1e40af' : '#334155' }}>{cls}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* RIGHT PANEL: LIVE A4 REAL-TIME INTERACTIVE PREVIEW */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#475569' }}>
            <Eye size={16} color="#2563eb" /> Live Real-Time A4 Interactive Preview ({useRealStudent ? 'Real Student Preview (Read-Only)' : 'Sample Student Mock'})
          </div>

          <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center', marginBottom: '-100px' }}>
            <div id="marks-card-designer-preview">
              <MarksCardRenderer
                reportCard={{
                  reportCardNumber: 'RC-2026-DEMO-001',
                  academicYear: '2026-27',
                  term: 'Final Evaluation',
                  student: useRealStudent ? {
                    name: 'Aarav Sharma',
                    studentId: 'STD-2026-0012',
                    admissionNumber: 'SCH-2026-0012',
                    rollNo: '05',
                    className: 'Class 5',
                    section: 'A',
                    dob: '12-Jan-2015',
                    parentName: 'Vijay Sharma'
                  } : {
                    name: 'Gururaja Kumar',
                    studentId: 'STD-2026-088',
                    admissionNumber: 'ADM-2026-042',
                    rollNo: '12',
                    className: selectedTemplate?.category === 'Kindergarten' ? 'UKG' : selectedTemplate?.category === 'PU_College' ? 'PUC-2' : 'Class 10',
                    section: 'A',
                    dob: '15-Aug-2012',
                    parentName: 'Ramesh Kumar'
                  },
                  totalMarks: 500,
                  obtainedMarks: 432,
                  percentage: 86.4,
                  overallGrade: 'A+',
                  result: 'PASS',
                  attendance: {
                    workingDays: 220,
                    presentDays: 206,
                    absentDays: 14,
                    percentage: 93.6
                  },
                  promotedClass: 'Class 11 - Science',
                  promotedAcademicYear: '2027-28',
                  classTeacherRemarks: 'Outstanding intellectual curiosity and exemplary behavior.',
                  principalRemarks: 'Promoted to next grade with distinction.'
                }}
                entries={selectedTemplate?.category === 'Kindergarten' || config?.templateKey === 'kindergarten_progress' ? [
                  { subjectName: 'Language Development', subjectCode: 'LANG101', maxMarks: 100, obtainedMarks: 90, grade: 'A+', halfYearly: 'A+', yearly: 'A', credits: 0, fa1: 18, fa2: 19 },
                  { subjectName: 'Numeracy & Quantitative Thinking', subjectCode: 'NUM102', maxMarks: 100, obtainedMarks: 85, grade: 'A', halfYearly: 'A', yearly: 'A+', credits: 0, fa1: 18, fa2: 19 },
                  { subjectName: 'Gross & Fine Motor Skills', subjectCode: 'MOT103', maxMarks: 100, obtainedMarks: 95, grade: 'O', halfYearly: 'O', yearly: 'O', credits: 0, fa1: 18, fa2: 19 },
                  { subjectName: 'Social & Emotional Development', subjectCode: 'SOC104', maxMarks: 100, obtainedMarks: 80, grade: 'B+', halfYearly: 'B+', yearly: 'A', credits: 0, fa1: 18, fa2: 19 },
                  { subjectName: 'Creativity & Aesthetic Skills', subjectCode: 'CRE105', maxMarks: 100, obtainedMarks: 90, grade: 'A+', halfYearly: 'A+', yearly: 'A+', credits: 0, fa1: 18, fa2: 19 }
                ] : [
                  { subjectName: 'Mathematics', subjectCode: 'MATH101', maxMarks: 100, obtainedMarks: 88, grade: 'A+', credits: 4, fa1: 18, fa2: 19, sa1: 51, halfYearly: 80, yearly: 88, theoryMarks: 65, practicalMarks: 23 },
                  { subjectName: 'Science', subjectCode: 'SCI102', maxMarks: 100, obtainedMarks: 92, grade: 'O', credits: 4, fa1: 19, fa2: 20, sa1: 53, halfYearly: 85, yearly: 92, theoryMarks: 68, practicalMarks: 24 },
                  { subjectName: 'English', subjectCode: 'ENG103', maxMarks: 100, obtainedMarks: 81, grade: 'A', credits: 3, fa1: 16, fa2: 17, sa1: 48, halfYearly: 75, yearly: 81, theoryMarks: 60, practicalMarks: 21 },
                  { subjectName: 'Social Studies', subjectCode: 'SST104', maxMarks: 100, obtainedMarks: 76, grade: 'B+', credits: 3, fa1: 15, fa2: 15, sa1: 46, halfYearly: 70, yearly: 76, theoryMarks: 56, practicalMarks: 20 },
                  { subjectName: 'Computer Science', subjectCode: 'CS105', maxMarks: 100, obtainedMarks: 95, grade: 'O', credits: 4, fa1: 20, fa2: 19, sa1: 56, halfYearly: 90, yearly: 95, theoryMarks: 70, practicalMarks: 25 }
                ]}
                branding={branding}
                institutionName={orgName || branding.organization?.orgName || 'SHARADA INTERNATIONAL SCHOOL'}
                institutionAddress={orgAddress || branding.organization?.address || ''}
                template={selectedTemplate?.templateKey || 'classic_academic'}
                templateConfig={config}
                gradingScale={[
                  { grade: 'A+', range: '90-100%' },
                  { grade: 'A', range: '80-89%' },
                  { grade: 'B+', range: '70-79%' },
                  { grade: 'B', range: '60-69%' },
                  { grade: 'C', range: '50-59%' },
                  { grade: 'D', range: '35-49%' },
                  { grade: 'F', range: 'Below 35%' }
                ]}
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportCardTemplateDesigner;
